import { Injectable } from '@angular/core';
import { User } from '../models/user';
import { Observable, ReplaySubject, Subject } from 'rxjs';
import { GoogleTagManagerService } from 'angular-google-tag-manager';
import { Organization } from '../models/organization';
import { distinctUntilChanged } from 'rxjs/operators';
import { AppInstance } from './app-instance.service';
import { DirectusPushNotificationRegister } from './push-notification.service';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  public userDataChangedSubject: Subject<boolean>;
  public user!: User;
  public selectedOrganizationObservable: Observable<Organization>;
  public areaHierarchyObservable: Observable<string[]>;
  public appInstanceSettingsObservable: Observable<AppInstance>;
  public pushNotificationTokenObservable: Observable<DirectusPushNotificationRegister>;
  public pushNotificationChangedSubject: Subject<boolean>;

  private selectedOrganizationSubject = new ReplaySubject<Organization>(1);
  private areaHierarchySubject = new ReplaySubject<string[]>(1);
  private appInstanceSettingsSubject = new ReplaySubject<AppInstance>(1);
  private pushNotificationTokenSubject = new ReplaySubject<DirectusPushNotificationRegister>();

  constructor(private gtmService: GoogleTagManagerService) {
    this.userDataChangedSubject = new Subject<boolean>();
    this.pushNotificationChangedSubject = new Subject<boolean>();
    this.selectedOrganizationObservable =
      this.selectedOrganizationSubject.pipe(distinctUntilChanged());
    this.areaHierarchyObservable = this.areaHierarchySubject.pipe(distinctUntilChanged());
    this.appInstanceSettingsObservable =
      this.appInstanceSettingsSubject.pipe(distinctUntilChanged());
    this.pushNotificationTokenObservable = this.pushNotificationTokenSubject;
  }

  getUser = () => this.user;

  restoreUserFromLocalStorage = () =>
    new Promise<User>((resolve) => {
      if (localStorage.getItem('user')) {
        this.user = new User();
        this.user.readFromLocalStorage();
        if (this.user.organizationId) {
          this.selectedOrganizationSubject.next({
            id: this.user.organizationId,
            enableSeutuluotsiAnnouncements: this.user.enableSeutuluotsiAnnouncements,
          });
        }
        resolve(this.user);
      } else {
        this.user = new User();
        resolve(this.user);
      }
    });

  saveUserToLocalStorage = () => {
    this.user.saveToLocalstorage();
  };

  setUserLanguage = (language: string) => {
    if (this.user.language === language) {
      return;
    }
    this.user.setLanguage(language);
  };

  setFavorites = async (contentId: string, componentType: string, name?: string) => {
    this.user.setFavorites(contentId, componentType);
    this.gtmService.pushTag({
      event: 'addToFavorite',
      type: componentType,
      name: name ?? contentId,
    });

    this.userDataChangedSubject.next(true);
  };

  setUserOrganization(organization: Organization) {
    this.user.setOrganization(organization);
    if (organization.id) {
      this.selectedOrganizationSubject.next({
        id: organization.id,
        enableSeutuluotsiAnnouncements: organization.enableSeutuluotsiAnnouncements,
      });
    }
  }

  setArea(selectedArea: string, areaHierarchy: string[]) {
    const areaId = this.user.areaId;
    let changed = false;
    if (selectedArea !== areaId) {
      changed = true;
    }
    this.user.setArea(selectedArea, areaHierarchy);
    if (changed) {
      this.pushNotificationChangedSubject.next(true);
    }
    if (areaHierarchy) {
      this.areaHierarchySubject.next(areaHierarchy);
    }
  }

  setAppInstance(settings: AppInstance) {
    this.user.setAppInstance(settings);
    if (settings) {
      this.appInstanceSettingsSubject.next(settings);
    }
  }

  async setPushNotification(settings: DirectusPushNotificationRegister) {
    let hasChanges = false;

    if (settings.token) {
      const token = this.user.userdata?.push_notification?.token;
      if (settings.token !== token) {
        hasChanges = true;
        this.user.setPushNotification(settings);
      }
    }

    if (settings.tags) {
      if (
        !this.comparePushNotificationTags(
          settings.tags,
          this.user.userdata?.push_notification?.tags,
        )
      ) {
        hasChanges = true;
        this.user.setPushNotification(settings);
      }
    }

    if ((settings.token || settings.tags) && hasChanges) {
      this.pushNotificationChangedSubject.next(true);
    }
  }

  private comparePushNotificationTags(a: string[], b: string[]): boolean {
    if (!a) {
      throw new Error('Comparing array failed');
    }
    if (!b) {
      return false;
    }
    if (a.length !== b.length) {
      return false;
    }
    for (const elem of a) {
      if (!b.includes(elem)) {
        return false;
      }
    }
    return true;
  }
}
